import React, {useState} from 'react';
import {observer} from 'mobx-react';
import './modal.scss';
import GeneralDialog from './GeneralDialog';
import {useStores} from '../../Hooks';
import {InfoIcon} from '../../assets/svg-icons/InfoIcon';
import Utility from '../../Utility';
import {WarningIcon} from '../../assets/svg-icons/WarningIcon';

const GeneralConfirmActionDialog = observer(() => {
    const {modalStore} = useStores();
    const [isConfirming, setIsConfirming] = useState(false);

    const {
        title,
        subtitle,
        bodyText,
        confirmText,
        cancelText,
        confirmActions,
        extraCloseActions,
        isDelete,
        persistModalData,
        noReset,
        goBackControl,
        isChangedColorButtons,
        noCancel,
        buttonClass: customButtonClass,
        isCaution,
    } = modalStore.modalData;

    const closeModal = () => {
        if (extraCloseActions) {
            extraCloseActions();
        }
        Utility.sleep(100).then(() => {
            if (persistModalData || noReset) {
                delete modalStore.modalData.title;
                delete modalStore.modalData.subtitle;
                delete modalStore.modalData.bodyText;
                delete modalStore.modalData.confirmText;
                delete modalStore.modalData.cancelText;
                delete modalStore.modalData.confirmActions;
                delete modalStore.modalData.extraCloseActions;
                delete modalStore.modalData.isDelete;
                delete modalStore.modalData.noReset;
                delete modalStore.modalData.goBackControl;
                delete modalStore.modalData.isChangedColorButtons;
                delete modalStore.modalData.noCancel;
                delete modalStore.modalData.buttonClass;
                delete modalStore.modalData.isCaution;
            } else modalStore.resetModalData();
        });
        modalStore.isGeneralConfirmActionDialogOpen = false;
        setIsConfirming(false);
    };

    const confirm = async () => {
        if (isConfirming) return;
        setIsConfirming(true);
        try {
            await confirmActions();
        } finally {
            closeModal();
        }
    };

    const getButtonClass = () => {
        if (customButtonClass) return customButtonClass;
        return isDelete ? 'warning' : 'notification';
    };

    const renderBody = () => (
        <div className="flex flex-col gap-3 text-sm leading-relaxed text-main-normal">
            {bodyText}
        </div>
    );

    const iconBadgeStyle = () => {
        const baseColor = isDelete
            ? 'var(--leavedeletenew-normal-color)'
            : isCaution ? 'var(--caution-warning-color)' : 'var(--classicbutton-normal-color)';
        return {backgroundColor: `color-mix(in srgb, ${baseColor} 15%, transparent)`};
    };

    const renderTitle = () => (
        <div className="flex flex-col">
            <div className="flex flex-row items-center gap-2.5">
                <div className="flex-shrink-0 p-2 rounded-full [&_svg]:w-6 [&_svg]:h-6" style={iconBadgeStyle()}>
                    {isCaution
                        ? <InfoIcon className="[&_path]:fill-[var(--caution-warning-color)]" />
                        : isDelete ? <WarningIcon /> : <InfoIcon />}
                </div>
                <div className="flex flex-col gap-0.5">
                    <div className="font-bold text-lg leading-6">{title}</div>
                    {subtitle && (
                        <div className="font-normal text-[13px] leading-[18px] text-[color:var(--main-normal-secondary-text-color,#8A8A8A)]">
                            {subtitle}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <GeneralDialog
            openControl="isGeneralConfirmActionDialogOpen"
            className="confirm-action-dialog"
            data-testid="confirm-action-dialog"
            onEnter={null}
            title={renderTitle()}
            body={renderBody()}
            isConfirmDisabled={isConfirming}
            onConfirm={confirm}
            confirmText={confirmText}
            buttonClass={getButtonClass()}
            specialOnClose={closeModal}
            extraClose={modalStore.modalData.extraClose}
            cancelText={cancelText}
            goBackControl={goBackControl}
            isChangedColorButtons={isChangedColorButtons}
            noCancel={noCancel}
        />
    );
});

export default GeneralConfirmActionDialog;
